<template>
  <div id="patinet-daily-report" class="wrrapper">
    <page-heading title="Patient Registration Report" class="report-heading">
      <template #content>
        <div class="report-filters no-print">
          <Select
            placeholder="Service Head"
            :loading="loading"
            :options="listDoctors"
            :input="handleFilter"
          />
          <Select
            placeholder="Service Name"
            :loading="loading"
            :options="
              listServices.map(item => ({
                id: item.serviceId,
                name: item.service.name
              }))
            "
            :input="filterByServiceName"
          />
          <Select
            placeholder="Doctor"
            :loading="loading"
            :options="filterItems"
            :input="filterByDoctors"
          />
          <DateRangePicker @update="updateDate" />
          <Select
            v-if="isAdmin"
            class="mr-2"
            styles="expenses-select"
            :input="handleBranchChange"
            :placeholder="
              brachData.length
                ? brachData && brachData.find(item => item.key == 1).name
                : ''
            "
            :options="brachData"
          />
          <div
            class="flex align-center cursor"
            @click="$modal.show('excel-report-modal')"
          >
            <span
              class="mdi mdi-download ml-2"
              style="font-size: 1.9rem;color: var(--accent-600);"
            ></span>
          </div>
          <ExportButtons @pdf="handleExportPdf" @excel="handleExportExcel" />
        </div>
      </template>
    </page-heading>
    <div id="patient--registrations">
      <data-table :loading="loading" :cols="columns" :rows="report" total>
        <template slot="action" slot-scope="{ actions }">
          <dropdown-menu
            v-if="isWithinPastWeek(actions.date)"
            :rowData="actions"
            :handleItemClick="handleItems"
            :items="discountItems"
          />
        </template>
      </data-table>
      <div class="report-summary">
        <div class="stats">
          <div class="stats__title">Gross Total</div>
          <div class="stats__value">{{ grossTotal.toLocaleString() }}</div>
        </div>
        <div class="stats">
          <div class="stats__title">Study Canceled</div>
          <div class="stats__value">
            {{ totalStudyCanceled.toLocaleString() }}
          </div>
        </div>
        <div class="stats">
          <div class="stats__title">Reservation Canceled</div>
          <div class="stats__value">
            {{ reservationCanceled.toLocaleString() }}
          </div>
        </div>
        <div class="stats">
          <div class="stats__title">Discount</div>
          <div class="stats__value">
            {{ discount }}
          </div>
        </div>
        <div class="stats stats--net">
          <div class="stats__title">Net Total</div>
          <div class="stats__value">
            {{ netTotal }}
          </div>
        </div>
      </div>
      <div class="print-summary flex justify-end pb-1 mr-1">
        <div>
          <div class="stats">
            <div class="stats__title">Gross Total:</div>
            <div class="stats__value">{{ grossTotal.toLocaleString() }}</div>
          </div>
          <div class="stats">
            <div class="stats__title">Study Canceled:</div>
            <div class="stats__value">
              {{ totalStudyCanceled.toLocaleString() }}
            </div>
          </div>
          <div class="stats">
            <div class="stats__title">Reservation Canceled:</div>
            <div class="stats__value">
              {{ reservationCanceled.toLocaleString() }}
            </div>
          </div>
          <div class="stats">
            <div class="stats__title">Total Discount:</div>
            <div class="stats__value">
              {{ discount }}
            </div>
          </div>
          <div class="stats">
            <div class="stats__title">Net Total:</div>
            <div class="stats__value">
              {{ netTotal }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <TablePrint
      :registerPatient="laterDiscountDetails && laterDiscountDetails.patient"
      :discount="laterDiscountDetails && laterDiscountDetails.discount"
      :rows="serviceRows"
      :total="totalAmount"
    />
    <modal
      name="discountModal"
      height="auto"
      :width="420"
      :adaptive="true"
      classes="discount-dialog-modal"
    >
      <div class="discount-dialog">
        <button
          type="button"
          class="discount-dialog__close"
          aria-label="Close"
          @click="closeDiscountModal"
        >
          <span class="mdi mdi-close"></span>
        </button>

        <div class="discount-dialog__icon">
          <span class="mdi mdi-tag-outline"></span>
        </div>

        <h3 class="discount-dialog__title">Apply discount</h3>
        <p class="discount-dialog__subtitle">
          Set a discount for this billed service.
        </p>

        <div v-if="discountRow" class="discount-dialog__details">
          <div class="discount-dialog__detail">
            <span class="discount-dialog__label">Patient</span>
            <span class="discount-dialog__value">{{
              discountRow.patientname || "—"
            }}</span>
          </div>
          <div class="discount-dialog__detail">
            <span class="discount-dialog__label">Service</span>
            <span class="discount-dialog__value">{{
              discountRow.service || "—"
            }}</span>
          </div>
          <div class="discount-dialog__detail">
            <span class="discount-dialog__label">Invoice</span>
            <span class="discount-dialog__value">{{
              discountRow.invoice || "—"
            }}</span>
          </div>
          <div class="discount-dialog__detail">
            <span class="discount-dialog__label">Rate</span>
            <span class="discount-dialog__value">{{
              formatNumbers(discountRow.rate || discountRow.amount || 0)
            }}</span>
          </div>
        </div>

        <div class="discount-dialog__field">
          <label
            class="discount-dialog__field-label"
            for="discount-amount-input"
            >Discount amount</label
          >
          <input
            id="discount-amount-input"
            class="discount-dialog__input"
            type="number"
            min="0"
            placeholder="0"
            v-model="discountAmount"
            @keyup.enter="handleDiscount"
          />
        </div>

        <div class="discount-dialog__actions">
          <button
            type="button"
            class="discount-dialog__btn discount-dialog__btn--secondary"
            @click="closeDiscountModal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="discount-dialog__btn discount-dialog__btn--primary"
            :disabled="loading"
            @click="handleDiscount"
          >
            <span v-if="!loading">Apply discount</span>
            <span v-else class="discount-dialog__loader"></span>
          </button>
        </div>
      </div>
    </modal>
    <ExcelReportModal />
  </div>
</template>

<script>
import DataTable from "@/components/DataTable";
import { formatedDate, formatNumbers, getBranchLogo } from "@/utils/helpers";
import DateRangePicker from "@/components/DateRangePIcker";
import printDiv from "@/utils/print";
import Select from "@/components/Select";
import ExcelReportModal from "@/components/ExcelReportModal.vue";
import DropdownMenu from "@/components/DropdownMenu";
import TablePrint from "@/components/TablePrint";
import ExportButtons from "@/components/ExportButtons.vue";
import { exportReportPdf, exportReportExcel } from "@/utils/reportExport";
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
export default {
  components: {
    DateRangePicker,
    DataTable,
    Select,
    ExcelReportModal,
    DropdownMenu,
    TablePrint,
    ExportButtons
  },
  data() {
    return {
      filters: {
        end_date: formatedDate(),
        start_date: formatedDate(),
        isOther: this.isOther,
        branchId: null,
        service_head: "",
        doctor_id: ""
      },
      columns: [
        { column: "serial", label: "#", width: "30" },
        {
          column: "date",
          label: "Date",
          sortable: true
        },
        {
          column: "patientname",
          label: "Patient Name",
          sortable: true
        },
        {
          column: "responsible",
          label: "Father Name",
          sortable: true
        },
        {
          column: "service",
          label: "Service",
          sortable: true
        },
        {
          column: "created_name",
          label: "Created By",
          sortable: true
        },
        {
          column: "amount",
          label: "Amount",
          formatAmount: true,
          sum: true,
          sortable: true
        },
        {
          column: "discount",
          label: "Discount",
          sortable: true
        },
        {
          column: "mr_no",
          label: "MR Number",
          sortable: true
        },
        {
          column: "status",
          label: "Status",
          sortable: true
        },
        {
          column: "invoice",
          label: "Invoice",
          sortable: true
        },
        {
          column: "action",
          label: "Action"
        }
      ],
      isAdmin: false,
      brachData: [],
      bussinessLogoUrl: "",
      branchLogoUrl: "",
      discountAmount: null,
      editHistoryId: null,
      discountRow: null,
      discountItems: ["Discount"]
    };
  },
  methods: {
    ...mapActions("reports", ["patientDailyReport"]),
    ...mapActions("expenses", ["fetchBranch"]),
    ...mapActions({
      doctors: "receptionist/listDoctors",
      doctorList: "doctors/doctorList",
      laterDiscount: "receptionist/laterDiscount"
    }),
    formatNumbers,
    isWithinPastWeek(date) {
      if (!date) return false;
      const rowDate = moment(date).startOf("day");
      const weekAgo = moment()
        .subtract(7, "days")
        .startOf("day");
      const today = moment().startOf("day");
      return rowDate.isSameOrAfter(weekAgo) && rowDate.isSameOrBefore(today);
    },
    closeDiscountModal() {
      this.discountAmount = null;
      this.editHistoryId = null;
      this.discountRow = null;
      this.$modal.hide("discountModal");
    },
    handleItems(val, rowData) {
      if (val !== "Discount") return;
      if (!this.isWithinPastWeek(rowData.date)) {
        this.$toasted
          .info("Discount is only available for records from the past week")
          .goAway("3000");
        return;
      }
      const historyId = rowData.historyId || rowData.history_id;
      if (!historyId) {
        this.$toasted
          .error("Unable to apply discount: missing history id")
          .goAway("3000");
        return;
      }
      this.editHistoryId = historyId;
      this.discountRow = rowData;
      this.discountAmount = rowData.discount || null;
      this.$modal.show("discountModal");
    },
    handleDiscount() {
      this.laterDiscount({
        historyId: this.editHistoryId,
        discount: this.discountAmount
      })
        .then(() => {
          const { mr_no } =
            this.laterDiscountDetails &&
            this.laterDiscountDetails.patient &&
            this.laterDiscountDetails.patient;
          const { invoice_no } = this.laterDiscountDetails || {};
          const details = {
            mrNo: mr_no,
            invoiceNo: invoice_no,
            imageUrl: this.bussinessLogoUrl,
            imageUrl2: this.branchLogoUrl
          };
          printDiv("TablePrint", details);
          this.closeDiscountModal();
          this.$toasted.success("Discount added successfully").goAway("3000");
          this.patientDailyReport(this.filters);
        })
        .catch(() => {
          this.closeDiscountModal();
        });
    },
    updateDate(val) {
      (this.filters.end_date = formatedDate(val.endDate)),
        (this.filters.start_date = formatedDate(val.startDate));
      this.patientDailyReport(this.filters);
    },
    exportInput() {
      return {
        title: "Patient Registration Report",
        dateRange: {
          start: this.filters.start_date,
          end: this.filters.end_date
        },
        printedBy: this.userInfo.full_name,
        businessLogoUrl: this.bussinessLogoUrl,
        branchLogoUrl: this.branchLogoUrl,
        cols: this.columns,
        rows: this.report,
        summary: [
          { label: "Gross Total", value: this.grossTotal.toLocaleString() },
          {
            label: "Study Canceled",
            value: this.totalStudyCanceled.toLocaleString()
          },
          {
            label: "Reservation Canceled",
            value: this.reservationCanceled.toLocaleString()
          },
          { label: "Discount", value: this.discount },
          { label: "Net Total", value: this.netTotal }
        ],
        fileName: "patient-registration-report"
      };
    },
    handleExportPdf() {
      exportReportPdf(this.exportInput());
    },
    handleExportExcel() {
      exportReportExcel(this.exportInput());
    },
    filterByDoctors(val) {
      const id = val && val.id !== null ? val.id : "";
      this.filters.doctor_id = id;
      this.patientDailyReport(this.filters);
    },
    filterByServiceName(val) {
      this.filters.service_name_id = val?.id;
      this.patientDailyReport(this.filters);
    },
    handleFilter(val) {
      const id = val && val.id !== null ? val.id : "";
      this.filters.service_head = id;
      this.patientDailyReport(this.filters);
    },
    handleBranchChange(val) {
      if (val && val != null) {
        const object = getBranchLogo(this.fetchUserBranch, val.key);
        this.branchLogoUrl = object;
        this.filters.branchId = val.key;

        this.patientDailyReport(this.filters);
      }
      if (val === null) {
        this.patientDailyReport(this.filters);
      }
    }
  },
  computed: {
    ...mapGetters({
      loading: "loading",
      patientReport: "reports/patientReport",
      listDoctors: "doctors/listDoctors",
      doctorsList: "receptionist/doctorsList",
      role: "auth/role",
      userInfo: "auth/userInfo",
      isOther: "auth/isOther",
      listServices: "services/listItems",
      fetchUserBranch: "expenses/fetchUserBranch",
      settings: "bussiness/settings",
      discountLater: "receptionist/discountLater"
    }),
    roleId() {
      const { id } = this.role && this.role.find(item => item);
      return id;
    },
    report() {
      const status = [1, 3, 5];
      return this.patientReport.map(item => ({
        status: status.includes(item.history_status) ? 3 : item.history_status,
        ...item
      }));
    },
    filterItems() {
      return this.doctorsList.map(item => ({
        name: item.full_name,
        id: item.id
      }));
    },
    grossTotal() {
      return this.report.reduce((accum, obj) => accum + obj.amount, 0);
    },
    netTotal() {
      const total = this.report
        .filter(item => item.history_status !== 7 && item.history_status !== 4)
        .reduce((accum, obj) => accum + obj.amount - obj.discount, 0);
      return formatNumbers(total);
    },
    discount() {
      const total = this.report
        .filter(item => item.history_status !== 7 && item.history_status !== 4)
        .reduce((accum, obj) => accum + obj.discount, 0);
      return formatNumbers(total) || 0;
    },
    totalStudyCanceled() {
      return this.report
        .filter(item => item.history_status === 7)
        .reduce((accum, obj) => accum + obj.amount, 0);
    },
    reservationCanceled() {
      return this.report
        .filter(item => item.history_status === 4)
        .reduce((accum, obj) => accum + obj.amount, 0);
    },
    laterDiscountDetails() {
      const arr =
        Array.isArray(this.discountLater) && this.discountLater.length > 0
          ? this.discountLater
          : [];
      const [newArr] = [...arr];
      return newArr;
    },
    totalAmount() {
      const discount =
        this.laterDiscountDetails && this.laterDiscountDetails.discount;
      const total =
        this.laterDiscountDetails &&
        this.laterDiscountDetails.charge &&
        this.laterDiscountDetails.charge.amount;
      return total - discount;
    },
    serviceRows() {
      const item =
        (this.laterDiscountDetails &&
          this.laterDiscountDetails.charge &&
          this.laterDiscountDetails.charge.service) ||
        {};
      const amount =
        this.laterDiscountDetails &&
        this.laterDiscountDetails.charge &&
        this.laterDiscountDetails.charge.amount;
      return [
        {
          name: item && item.name,
          price: amount,
          subtotal: amount
        }
      ];
    }
  },
  async mounted() {
    await this.fetchBranch();
    await this.fetchUserBranch?.map(elem => {
      this.brachData.push({ name: elem.name, key: elem.id });
    });

    this.branchLogoUrl = getBranchLogo(
      this.fetchUserBranch,
      this.userInfo?.userBranchId
    );

    this.bussinessLogoUrl = this.userInfo?.bussinessLogoUrl;

    this.isAdmin = this.role.some(({ name }) => name === "admin");
    this.patientDailyReport(this.filters);
    this.doctorList("servicehead");
    this.doctors();
    this.$store.dispatch("services/listServices", this.isOther);
  }
};
</script>

<style>
@import "../../assets/css/print.css";

#patinet-daily-report .report-heading {
  border-bottom: 1px solid var(--line);
}

#patinet-daily-report .right-aligned-items {
  min-width: 0;
}

#patinet-daily-report .report-filters {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
  max-width: 980px;
}

#patinet-daily-report .report-filters > * {
  margin: 0 !important;
}

#patinet-daily-report .report-filters .custom-select {
  min-width: 170px;
}

#patient--registrations {
  padding: 18px 24px 24px;
}

#patinet-daily-report .report-summary {
  display: grid;
  grid-template-columns: repeat(5, minmax(130px, 1fr));
  gap: 10px;
  margin-top: 16px;
}

#patinet-daily-report .report-summary .stats {
  display: block;
  padding: 12px 14px;
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  background: var(--surface);
}

#patinet-daily-report .report-summary .stats__title {
  width: auto;
  margin-bottom: 6px;
  color: var(--muted-soft);
  font-size: var(--fs-eyebrow);
  font-weight: 700;
  letter-spacing: var(--tracking-eyebrow);
  line-height: 1.2;
  text-transform: uppercase;
}

#patinet-daily-report .report-summary .stats__value {
  color: var(--ink-900);
  font-size: var(--fs-h2);
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: 0;
}

#patinet-daily-report .report-summary .stats--net {
  border-color: rgba(109, 84, 199, 0.32);
  background: var(--accent-050);
}

.print-summary {
  display: none;
}

.stats {
  display: flex;
  align-items: center;
}

.stats .stats__title {
  width: 200px;
  font-weight: 600;
  font-size: var(--fs-body);
  line-height: 30px;
  letter-spacing: 0.03em;
  text-transform: capitalize;
  color: var(--muted);
}

.stats .stats__value {
  font-weight: 400;
  font-size: var(--fs-body);
  line-height: 30px;
  letter-spacing: 0.03em;
  text-transform: capitalize;
  color: var(--text);
}

@media screen and (max-width: 1180px) {
  #patinet-daily-report .report-filters {
    justify-content: flex-start;
    margin-top: 12px;
  }

  #patinet-daily-report .report-summary {
    grid-template-columns: repeat(3, minmax(130px, 1fr));
  }
}

@media screen and (max-width: 720px) {
  #patient--registrations {
    padding: 16px;
  }

  #patinet-daily-report .report-filters,
  #patinet-daily-report .report-filters .custom-select {
    width: 100%;
  }

  #patinet-daily-report .report-summary {
    grid-template-columns: 1fr;
  }
}

@media print {
  #patinet-daily-report .report-summary {
    display: none;
  }

  .print-summary {
    display: flex;
  }

  #TablePrint {
    display: block;
  }
}

#TablePrint {
  display: none;
}
</style>

<style>
.discount-dialog-modal {
  border-radius: 14px !important;
  overflow: hidden !important;
  box-shadow: 0 18px 48px rgba(26, 19, 54, 0.16) !important;
  border: 1px solid var(--line) !important;
}

.discount-dialog {
  position: relative;
  padding: 28px 28px 24px;
  background: var(--surface, #fff);
}

.discount-dialog__close {
  position: absolute;
  top: 14px;
  right: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--muted-soft, #8b8798);
  cursor: pointer;
}

.discount-dialog__close:hover {
  background: var(--surface-2, #f4f3f8);
  color: var(--text, #1f1a2e);
}

.discount-dialog__close .mdi {
  font-size: 1.2rem;
  line-height: 1;
}

.discount-dialog__icon {
  width: 52px;
  height: 52px;
  margin: 0 auto 14px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(109, 84, 199, 0.12);
  color: var(--accent-600, #6d54c7);
}

.discount-dialog__icon .mdi {
  font-size: 1.6rem;
  line-height: 1;
}

.discount-dialog__title {
  margin: 0 0 6px;
  text-align: center;
  font-size: 1.125rem;
  font-weight: 600;
  line-height: 1.35;
  color: var(--text, #1f1a2e);
  text-transform: none;
  letter-spacing: 0;
}

.discount-dialog__subtitle {
  margin: 0 auto 20px;
  max-width: 280px;
  text-align: center;
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--muted, #6b657a);
}

.discount-dialog__details {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 18px;
  padding: 14px 16px;
  border-radius: 10px;
  background: var(--surface-2, #f6f5f9);
  border: 1px solid var(--line, #e6e2ef);
}

.discount-dialog__detail {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.discount-dialog__label {
  flex: 0 0 auto;
  min-width: 64px;
  font-size: 0.8125rem !important;
  font-weight: 500 !important;
  line-height: 1.4 !important;
  color: var(--muted, #6b657a) !important;
}

.discount-dialog__value {
  flex: 1 1 auto;
  text-align: right;
  font-size: 0.875rem !important;
  font-weight: 600 !important;
  line-height: 1.4 !important;
  color: var(--text, #1f1a2e) !important;
  word-break: break-word;
}

.discount-dialog__field {
  margin-bottom: 22px;
}

.discount-dialog__field-label {
  display: block;
  margin-bottom: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text, #1f1a2e);
}

.discount-dialog__input {
  width: 100%;
  height: 44px;
  padding: 0 14px;
  border: 1px solid var(--line-strong, #cdd6e0);
  border-radius: 10px;
  background: #fff;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 500;
  color: var(--text, #1f1a2e);
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.discount-dialog__input:focus {
  border-color: var(--accent-600, #6d54c7);
  box-shadow: 0 0 0 3px rgba(109, 84, 199, 0.15);
}

.discount-dialog__actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.discount-dialog__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 120px;
  height: 40px;
  padding: 0 18px;
  border-radius: 10px;
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    box-shadow 0.15s ease;
}

.discount-dialog__btn--secondary {
  border: 1px solid var(--line-strong, #cdd6e0);
  background: #fff;
  color: var(--text, #1f1a2e);
}

.discount-dialog__btn--secondary:hover {
  background: var(--surface-2, #f4f3f8);
}

.discount-dialog__btn--primary {
  border: 1px solid transparent;
  background: var(--grad-accent, var(--accent-600, #6d54c7));
  color: #fff;
  box-shadow: 0 2px 8px rgba(109, 84, 199, 0.28);
}

.discount-dialog__btn--primary span {
  color: #fff !important;
  font-size: inherit !important;
  font-weight: inherit !important;
}

.discount-dialog__btn--primary:hover:not(:disabled) {
  box-shadow: 0 4px 12px rgba(109, 84, 199, 0.34);
}

.discount-dialog__btn--primary:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.discount-dialog__loader {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.45);
  border-top-color: #fff;
  border-radius: 50%;
  animation: discount-dialog-spin 0.8s linear infinite;
}

@keyframes discount-dialog-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
